import React, { useState, useEffect } from 'react';
import { Compass, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { RadioCard } from '../components/RadioCard';
import { cn } from '../utils/cn';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { CoreValue } from '../types';
import { ProtectedLayout } from '../components/ProtectedLayout';

const predefinedValues: CoreValue[] = [
  'Achievement', 'Adventure', 'Authenticity', 'Boldness', 'Connection',
  'Contribution', 'Courage', 'Creativity', 'Curiosity', 'Dancing',
  'Discovery', 'Excellence', 'Exercise', 'Faith', 'Family',
  'Friendship', 'Fun', 'Growth', 'Helping Others', 'Imagination',
  'Integrity', 'Kindness', 'Movement', 'Music', 'Nature',
  'Passion', 'Play', 'Pleasure', 'Purpose', 'Responsibility',
  'Spontaneity', 'Structures', 'Success', 'Systems', 'Teaching', 'Wonder'
];

export function UpdateCoreValues() {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedValues, setSelectedValues] = useState<CoreValue[]>([]);
  const [customValues, setCustomValues] = useState(['', '', '']);
  const [activeCustomIndex, setActiveCustomIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [coreWord, setCoreWord] = useState('');

  useEffect(() => {
    async function fetchCurrentValues() {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
          navigate('/');
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('core_value_1, core_value_2, core_value_3, name, email, core_word')
          .eq('id', session.user.id)
          .single();

        if (profileError) throw profileError;

        if (profile) {
          const values = [
            profile.core_value_1,
            profile.core_value_2,
            profile.core_value_3
          ].filter(Boolean) as CoreValue[];

          setSelectedValues(values);
          if (profile.name) setFullName(profile.name);
          if (profile.email) setEmail(profile.email);
          if (profile.core_word) setCoreWord(profile.core_word);
        }
      } catch (error: any) {
        console.error('Error fetching core values:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchCurrentValues();
  }, [navigate]);

  const handleValueSelect = (value: CoreValue) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(prev => prev.filter(v => v !== value));
    } else if (selectedValues.length < 3) {
      setSelectedValues(prev => [...prev, value]);
    }
  };

  const handleCustomValueChange = (index: number, value: string) => {
    setCustomValues(prev => {
      const newValues = [...prev];
      newValues[index] = value;
      return newValues;
    });
    setActiveCustomIndex(index);
  };

  const handleCustomValueConfirm = (index: number) => {
    const value = customValues[index].trim();
    if (value && selectedValues.length < 3) {
      setSelectedValues(prev => [...prev, value]);
      setCustomValues(prev => {
        const newValues = [...prev];
        newValues[index] = '';
        return newValues;
      });
    }
    setActiveCustomIndex(null);
  };

  const sendWebhook = async () => {
    try {
      const webhookUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ghl-webhook`;

      const {
        data: { session },
        error: sessionError
      } = await supabase.auth.getSession();

      if (sessionError || !session?.user) {
        console.error('Missing session or user:', sessionError);
        return;
      }

      const payload = {
        userId: session.user.id
      };

      console.log('[sendWebhook] Sending to:', webhookUrl);
      console.log('[sendWebhook] Payload:', payload);

      const res = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Webhook failed:', errorText);
      }
    } catch (err) {
      console.error('Webhook error:', err);
    }
  };

  const handleUpdate = async () => {
    if (selectedValues.length !== 3) return;

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          core_value_1: selectedValues[0],
          core_value_2: selectedValues[1],
          core_value_3: selectedValues[2]
        })
        .eq('id', session.user.id);

      if (updateError) throw updateError;

      await sendWebhook();

      const returnPath = location.state?.from === '/profile' ? '/profile' : '/dashboard';
      navigate(returnPath);
    } catch (error: any) {
      console.error('Error updating core values:', error);
      setError(error.message || 'Failed to update core values');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="text-[#236caf]">Loading...</div>
      </div>
    );
  }

  return (
    <ProtectedLayout>
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl w-full bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Compass className="w-8 h-8 text-[#236caf]" />
            <h1 className="text-3xl font-bold text-[#004060]">Update Core Values</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(location.state?.from || '/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <p className="text-xl text-gray-600 mb-8">
          Choose 3 values that represent you:
          {selectedValues.length > 0 && (
            <span className="text-[#236caf] font-medium ml-2">
              ({selectedValues.length}/3 selected)
            </span>
          )}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {predefinedValues.map((value) => (
            <RadioCard
              key={value}
              label={value}
              selected={selectedValues.includes(value)}
              onClick={() => handleValueSelect(value)}
              className={cn(
                selectedValues.length === 3 && !selectedValues.includes(value) && 'opacity-50 cursor-not-allowed'
              )}
            />
          ))}

          {[0, 1, 2].map((index) => (
            <div
              key={`custom-${index}`}
              className={cn(
                'p-4 rounded-lg border-2 transition-all duration-200',
                activeCustomIndex === index ? 'border-[#236caf] bg-blue-50' : 'border-dashed border-gray-300',
                selectedValues.length === 3 && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex flex-col gap-2">
                <span className="text-lg font-medium text-gray-700">Custom Value {index + 1}</span>
                <input
                  type="text"
                  value={customValues[index]}
                  onChange={(e) => handleCustomValueChange(index, e.target.value)}
                  onFocus={() => setActiveCustomIndex(index)}
                  onBlur={() => handleCustomValueConfirm(index)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCustomValueConfirm(index);
                    }
                  }}
                  placeholder="Enter custom value..."
                  className="w-full p-2 rounded border border-gray-300 focus:border-[#236caf] focus:ring-2 focus:ring-[#236caf]"
                  disabled={selectedValues.length === 3}
                />
              </div>
            </div>
          ))}
        </div>

        {selectedValues.length > 0 && (
          <div className="mb-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-gray-700 mb-2">Selected Values:</h3>
            <div className="flex flex-wrap gap-2">
              {selectedValues.map((value, index) => (
                <div
                  key={index}
                  className="px-3 py-1 bg-white rounded-full border border-[#236caf] text-[#236caf] flex items-center gap-2"
                >
                  {value}
                  <button
                    onClick={() => setSelectedValues(prev => prev.filter(v => v !== value))}
                    className="text-[#236caf] hover:text-[#004060]"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={handleUpdate}
          disabled={selectedValues.length !== 3 || isSubmitting}
          className={cn(
            'w-full',
            (selectedValues.length !== 3 || isSubmitting) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isSubmitting ? 'Updating...' : 'Update Values'}
        </Button>
      </div>
    </div>
    </ProtectedLayout>
  );
}
